/* eslint-disable @typescript-eslint/no-explicit-any */
import { useRadio } from "@shared/hooks/useRadio";
import { useStyleConfig } from "@shared/hooks/useStyleConfig";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Box from "@ui-kit/layout/Box/Box.web";
import inputHeights from "@shared/theme/props/inputHeights";
import {
  buttonPaintMap,
  buttonPaintMapActive,
  buttonPaintMapHover,
  buttonPaintTextColor,
  type ButtonPaintMapKeyType,
} from "@shared/theme/props/buttonPaintMap";
import { ButtonRadioSharedProps } from "@ui-kit/form/ButtonRadio/ButtonRadio.props";

const { xs, sm, md, lg } = inputHeights;

const ButtonRadio: React.FC<ButtonRadioSharedProps> = ({
  size = "md",
  labelProps,
  colorPalette = "tertiary",
  variant = "outline",
  fontWeight = "normal",
  fontSize = "sm",
  minWidth = "90px",
  children,
  ...rest
}) => {
  const { getInputProps, getCheckboxProps } = useRadio(rest);
  const radioButtonSize = (size: string) => {
    switch (size) {
      case "xs":
        return xs;
      case "sm":
        return sm;
      case "md":
        return md;
      case "lg":
        return lg;
      default:
        return md;
    }
  };

  const input = getInputProps();
  const checkbox: ReturnType<typeof getCheckboxProps> = getCheckboxProps();

  const buttonStyles = useStyleConfig("Button", {
    variant,
    colorPalette,
  });

  const paint = (colorPalette || "tertiary") as ButtonPaintMapKeyType;

  const getTextColor = (): string => {
    if (colorPalette === "ghost") return "font.dark";
    if (variant === "outline") return `${colorPalette}.800`;
    return buttonStyles.color as string;
  };

  const getHoverStyles = (): Record<string, unknown> => {
    if (variant === "outline") {
      return {
        bg: buttonPaintMap[paint],
        color: buttonPaintTextColor[paint],
        _active: { bg: buttonPaintMapActive[paint] },
      };
    }
    return {
      bg: buttonPaintMapHover[paint],
      _active: { bg: buttonPaintMapActive[paint] },
    };
  };

  const styles = {
    ...buttonStyles,
    display: "inline-flex",
    justifyContent: "center",
    alignItems: "center",
    cursor: "pointer",
    color: getTextColor(),
    border: variant === "outline" ? "1px solid" : undefined,
    borderColor: variant === "outline" ? `${colorPalette}.800` : undefined,
    borderRadius: "md",
    transition: "background 0.2s, color 0.2s, border-color 0.2s",
  };

  return (
    <Box as="label" {...labelProps}>
      <input
        {...input}
        style={{
          position: "absolute",
          width: "1px",
          height: "1px",
          padding: 0,
          margin: "-1px",
          overflow: "hidden",
          clip: "rect(0, 0, 0, 0)",
          whiteSpace: "nowrap",
          border: 0,
        }}
      />
      <Flex
        {...(checkbox as any)}
        {...styles}
        _hover={getHoverStyles()}
        _checked={{
          backgroundColor: buttonPaintMapHover[paint],
          color: buttonPaintTextColor[paint],
          _hover: {
            bg: buttonPaintMapActive[paint],
          },
        }}
        height={radioButtonSize(size)}
        fontWeight={fontWeight}
        fontSize={fontSize}
        minWidth={minWidth}
        {...rest}
      >
        {children}
      </Flex>
    </Box>
  );
};

export default ButtonRadio;
